<section class="comments" [attr.aria-busy]="loading()">
  <p-toast />
  <p-confirmDialog />

  <!-- Polite live region: announces SSE-driven new comments to screen readers without
       interrupting the current focus. Visually hidden but reachable by assistive tech. -->
  <span class="sr-only" aria-live="polite" aria-atomic="true">{{ liveRegionMessage() }}</span>

  <header class="comments__header">
    @if (canCreateComments()) {
      <p-button
        class="comments__new-comment"
        [label]="(composerOpen() ? 'GENERAL.CANCEL' : 'COMMENTS.NEW_COMMENT') | translate"
        [icon]="composerOpen() ? 'pi pi-times' : 'pi pi-plus'"
        severity="secondary"
        [outlined]="!composerOpen()"
        [text]="composerOpen()"
        size="small"
        (onClick)="onToggleComposer()" />
    }

    <p-select
      class="comments__sort"
      [options]="sortOptions"
      [ngModel]="selectedSort()"
      (ngModelChange)="onSortChange($event)"
      optionLabel="labelKey"
      optionValue="value"
      [attr.aria-label]="'COMMENTS.SORT.LABEL' | translate"
      appendTo="body">
      <ng-template let-option pTemplate="item">{{ option.labelKey | translate }}</ng-template>
      <ng-template let-option pTemplate="selectedItem">{{ option.labelKey | translate }}</ng-template>
    </p-select>
  </header>

  <div class="comments__body">
    @if (composerOpen()) {
      <tf-comment-composer
        class="comments__composer"
        [targetType]="composerTargetType()"
        [targetId]="targetId()"
        [autofocus]="true"
        [showCancel]="true"
        (created)="onCreatedFromMainComposer($event)"
        (cancelled)="onToggleComposer()"
        (draftChange)="onDraftChange($event)" />
    }

    @if (loading()) {
    <div class="comments__loading" role="status">
      <p-progressspinner styleClass="comments__spinner" />
      <span class="sr-only">{{ 'GENERAL.LOADING' | translate }}</span>
    </div>
  } @else if (hasError()) {
    <div class="comments__error" role="alert">
      <span>{{ 'COMMENTS.LOAD_ERROR' | translate }}</span>
    </div>
  } @else if (isEmpty()) {
    <div class="comments__empty">
      <span>{{ 'COMMENTS.EMPTY' | translate }}</span>
    </div>
  } @else {
    <ol #listEl class="comments__list">
      @for (vm of viewModel(); track vm.comment.id) {
        <li
          class="comments__item"
          [class.comments__item--thread-start]="vm.isThreadStart"
          [class.comments__item--just-arrived]="arrivedIds().has(vm.comment.id)"
          [class.comments__item--focused]="focusedCommentId() === vm.comment.id"
          [attr.data-comment-id]="vm.comment.id">
          <tf-comment-row
            [comment]="vm.comment"
            [canReply]="canCreateComments()"
            [isReply]="vm.isReply"
            [isLastReply]="vm.isLastReply"
            [hasReplies]="vm.hasReplies"
            [replyToAuthor]="vm.replyToAuthor"
            [currentUserId]="currentUserId()"
            [canModerate]="canModerateComments()"
            [isEditing]="editingId() === vm.comment.id"
            (replyRequested)="onReplyRequested($event)"
            (editRequested)="onEditRequested($event)"
            (deleteRequested)="onDeleteRequested($event)"
            (restoreRequested)="onRestoreRequested($event)"
            (viewHistoryRequested)="onViewHistoryRequested($event)"
            (editSaved)="onEditSaved($event)"
            (editCancelled)="onEditCancelled()"
            (markReadRequested)="onMarkRead($event)"
            (markUnreadRequested)="onMarkUnread($event)"
            (copyLinkRequested)="onCopyLink($event)"
            (reactionToggled)="onReactionToggled($event)"
            (attachmentOpenRequested)="onAttachmentOpenRequested($event)"
            (editDraftChange)="onDraftChange($event)" />
          @if (replyingTo() === vm.comment.id) {
            <tf-comment-composer
              class="comments__reply-composer"
              [targetType]="composerTargetType()"
              [targetId]="targetId()"
              [parentCommentId]="vm.comment.rootCommentId"
              [autofocus]="true"
              [showCancel]="true"
              (created)="onCreated($event)"
              (cancelled)="onReplyCancelled()"
              (draftChange)="onDraftChange($event)" />
          }
        </li>
      }
    </ol>

    @if (hasMore()) {
      <p-button
        class="comments__load-more"
        [label]="'COMMENTS.LOAD_MORE' | translate"
        severity="secondary"
        [outlined]="true"
        size="small"
        [loading]="loadingMore()"
        [disabled]="loadingMore()"
        (onClick)="onLoadMore()" />
    }
  }
  </div>

  <tf-comment-edit-history-dialog
    [visible]="historyVisible()"
    [commentId]="historyOpenForId()"
    (visibleChange)="onHistoryVisibilityChange($event)" />
</section>
